import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Loader2, Plus, Pencil, Trash2, ListChecks, Archive, ArchiveRestore } from 'lucide-react';
import { purchasingAPI, ReasonCode, fetchAllPages } from '../../../lib/api';
import { toast } from 'sonner';
import { ReasonCodeAuditHistory } from './ReasonCodeAuditHistory';

const CATEGORY_OPTIONS = [
  { value: 'delay', label: 'Delay' },
  { value: 'quality', label: 'Quality Issue' },
  { value: 'quantity', label: 'Quantity Change' },
  { value: 'price', label: 'Price Change' },
  { value: 'date_change', label: 'Date Change' },
  { value: 'technical', label: 'Technical Issue' },
  { value: 'supplier', label: 'Supplier Issue' },
  { value: 'customer', label: 'Customer Issue' },
  // Customer review 2026-07-08: reasons offered in the schedule Put On Hold
  // dialog (e.g. account on stop, no empty stillages, engineering/PCN).
  { value: 'hold', label: 'Hold' },
  { value: 'other', label: 'Other' },
];

const CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  CATEGORY_OPTIONS.map(o => [o.value, o.label])
);

type FilterView = 'active' | 'archived' | 'all';

const FILTER_OPTIONS: { value: FilterView; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
];

interface FormState {
  code: string;
  description: string;
  category: string;
  is_active: boolean;
  is_archived: boolean;
}

export function ReasonCodesSection() {
  const [codes, setCodes] = useState<ReasonCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ReasonCode | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>({ code: '', description: '', category: '', is_active: true, is_archived: false });
  const [deleteTarget, setDeleteTarget] = useState<ReasonCode | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [filter, setFilter] = useState<FilterView>('active');

  const fetchCodes = useCallback(async () => {
    try {
      setLoading(true);
      // Walk pages — the backend clamps page_size, so one request silently truncated.
      const data = await fetchAllPages<ReasonCode>((page) => purchasingAPI.getReasonCodes({ page_size: 100, page }));
      setCodes(data);
    } catch {
      toast.error('Failed to load reason codes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const openDialog = (rc?: ReasonCode) => {
    if (rc) {
      setEditing(rc);
      setForm({ code: rc.code, description: rc.description, category: rc.category, is_active: rc.is_active, is_archived: rc.is_archived });
    } else {
      setEditing(null);
      setForm({ code: '', description: '', category: '', is_active: true, is_archived: false });
    }
    setDialogOpen(true);
  };

  const sortCodes = (list: ReasonCode[]) =>
    [...list].sort((a, b) => a.category.localeCompare(b.category) || a.code.localeCompare(b.code));

  const saveCode = async () => {
    // CR-5886: only Code and Category are required — Description is optional.
    if (!form.code.trim() || !form.category) {
      toast.error('Code and category are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        // Don't let the edit form silently un-archive a code via its status toggle.
        const payload = form.is_archived
          ? { code: form.code, description: form.description, category: form.category }
          : { code: form.code, description: form.description, category: form.category, is_active: form.is_active };
        const res = await purchasingAPI.updateReasonCode(editing.id, payload);
        setCodes(prev => sortCodes(prev.map(c => (c.id === editing.id ? res.data : c))));
        toast.success('Reason code updated');
      } else {
        const res = await purchasingAPI.createReasonCode({
          code: form.code, description: form.description, category: form.category, is_active: form.is_active,
        });
        setCodes(prev => sortCodes([...prev, res.data]));
        toast.success('Reason code created');
      }
      setDialogOpen(false);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: Record<string, string[]> } };
      const detail = axiosErr.response?.data;
      if (detail && typeof detail === 'object') {
        const msgs = Object.values(detail).flat().join(', ');
        toast.error(msgs || 'Failed to save reason code');
      } else {
        toast.error('Failed to save reason code');
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setDeleting(true);
    try {
      const res = await purchasingAPI.deleteReasonCode(id);
      const body = res.data;
      if (body && typeof body === 'object' && 'archived' in body && body.archived) {
        // CR-5886: code was in use, so it was archived (not deleted) — keep it
        // in the list with its updated archived state.
        setCodes(prev => sortCodes(prev.map(c => (c.id === id ? body.reason_code : c))));
        toast.success('Reason code is in use — archived instead of deleted');
      } else {
        setCodes(prev => prev.filter(c => c.id !== id));
        toast.success('Reason code deleted');
      }
      setDeleteTarget(null);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 404) {
        setCodes(prev => prev.filter(c => c.id !== id));
        toast.success('Reason code already removed');
        setDeleteTarget(null);
      } else {
        toast.error('Failed to delete reason code');
      }
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async (rc: ReasonCode) => {
    setCodes(prev => prev.map(c => c.id === rc.id ? { ...c, is_active: !c.is_active } : c));
    try {
      await purchasingAPI.updateReasonCode(rc.id, { is_active: !rc.is_active });
    } catch {
      toast.error('Failed to update reason code');
      await fetchCodes();
    }
  };

  // CR-5886: un-archive a code — it comes back Disabled so an admin must
  // consciously re-enable it before it is selectable for new transactions.
  const restoreCode = async (rc: ReasonCode) => {
    try {
      const res = await purchasingAPI.restoreReasonCode(rc.id);
      setCodes(prev => sortCodes(prev.map(c => (c.id === rc.id ? res.data : c))));
      toast.success('Reason code restored — now Disabled; enable it to use again');
    } catch {
      toast.error('Failed to restore reason code');
    }
  };

  const visibleCodes = codes.filter(c =>
    filter === 'all' ? true : filter === 'archived' ? c.is_archived : !c.is_archived
  );

  if (loading && codes.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ListChecks className="h-5 w-5" />
                Reason Code (RCC)
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Reason codes appear in change request, decline, and hold workflows
              </p>
            </div>
            <Button size="sm" onClick={() => openDialog()}>
              <Plus className="h-4 w-4 mr-1" />
              Add Reason Code
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* CR-5886: Active vs Archived filter */}
          <div className="flex items-center gap-1 mb-4">
            {FILTER_OPTIONS.map(opt => (
              <Button
                key={opt.value}
                variant={filter === opt.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>

          {visibleCodes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ListChecks className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{filter === 'archived' ? 'No archived reason codes' : 'No reason codes yet'}</p>
              {filter !== 'archived' && (
                <p className="text-sm mt-1">Add reason codes so users can select them in workflows</p>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="w-32 text-center">Status</TableHead>
                  <TableHead className="w-28 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleCodes.map(rc => (
                  <TableRow key={rc.id}>
                    <TableCell className="font-medium">{rc.code}</TableCell>
                    <TableCell>{rc.description || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell>{CATEGORY_LABELS[rc.category] || rc.category}</TableCell>
                    <TableCell className="text-center">
                      {rc.is_archived ? (
                        <Badge variant="secondary" className="bg-rose-100 text-rose-700 hover:bg-rose-100 gap-1">
                          <Archive className="h-3 w-3" /> Archived
                        </Badge>
                      ) : (
                        <div className="inline-flex items-center gap-2">
                          <Badge
                            variant={rc.is_active ? 'default' : 'secondary'}
                            className={rc.is_active ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}
                          >
                            {rc.is_active ? 'Enabled' : 'Disabled'}
                          </Badge>
                          <Switch checked={rc.is_active} onCheckedChange={() => toggleActive(rc)} />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDialog(rc)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {rc.is_archived ? (
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Restore (un-archive)" onClick={() => restoreCode(rc)}>
                            <ArchiveRestore className="h-3.5 w-3.5" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setDeleteTarget(rc)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Reason Code' : 'New Reason Code'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Code</Label>
              <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="e.g. DEL01" />
            </div>
            <div>
              <Label>Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="e.g. Material shortage" />
            </div>
            <div>
              <Label>Category</Label>
              <select
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="">Select category...</option>
                {CATEGORY_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center justify-between">
              <Label>Status</Label>
              {editing && form.is_archived ? (
                <div className="inline-flex items-center gap-2">
                  <Badge variant="secondary" className="bg-rose-100 text-rose-700 hover:bg-rose-100 gap-1">
                    <Archive className="h-3 w-3" /> Archived
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => { if (editing) { await restoreCode(editing); setDialogOpen(false); } }}
                  >
                    <ArchiveRestore className="h-3.5 w-3.5 mr-1" /> Restore
                  </Button>
                </div>
              ) : (
                <div className="inline-flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{form.is_active ? 'Enabled' : 'Disabled'}</span>
                  <Switch checked={form.is_active} onCheckedChange={checked => setForm({ ...form, is_active: checked })} />
                </div>
              )}
            </div>

            {/* CR-5886: audit trail visible when editing an existing code */}
            {editing && <ReasonCodeAuditHistory reasonCodeId={editing.id} />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveCode} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete / Archive Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={open => { if (!open && !deleting) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{deleteTarget?.is_used ? 'Archive Reason Code' : 'Confirm Delete'}</DialogTitle>
          </DialogHeader>
          {deleteTarget?.is_used ? (
            <p className="text-sm text-muted-foreground">
              <strong>"{deleteTarget?.code}"</strong> is used on {deleteTarget?.usage_count} record(s) and cannot be permanently deleted.
              It will be <strong>archived</strong> instead — it stays visible on historical records but can no longer be selected for new transactions.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Delete reason code <strong>"{deleteTarget?.code}"</strong>? This cannot be undone.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
            {deleteTarget?.is_used ? (
              <Button variant="default" onClick={confirmDelete} disabled={deleting}>
                {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Archive className="h-4 w-4 mr-2" />} Archive
              </Button>
            ) : (
              <Button variant="destructive" onClick={confirmDelete} disabled={deleting}>
                {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />} Delete
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
